// ShellHeader.jsx — Shared chrome across Easy Mode and Workspace

import React from 'react';

import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useIsMobile } from 'hooks/useDeviceDetect';

import Button from 'components/button/Button';
import LogoSmall from 'components/logo/small/LogoSmall';
import ShellAddButton from 'main/shell/ShellAddButton';
import SidebarSwitcher from 'main/sidebar/switcher/SidebarSwitcher';

import './ShellHeader.css';

/**
 * ShellHeader
 *
 * Consistent top chrome: logo, Add, base switcher.
 * Logo row hosts the sidebar rail collapse / expand control.
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {boolean} [props.showBaseSwitcher=true] - Show base switcher row.
 * @param {ReactNode} [props.beforeSwitcher] - Optional content above the base switcher
 *   (e.g. base cover photo).
 * @param {ReactNode} [props.trailing] - Optional actions on the logo row (after Add).
 * @returns {ReactNode}
 */
const ShellHeader = React.memo(({
  className = '',
  showBaseSwitcher = true,
  beforeSwitcher = null,
  trailing = null,
}) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const {
    isSidebarRailCompact,
    sidebarRailNarrowViewport,
    expandSidebarRail,
    collapseSidebarRail,
  } = useUI();
  const compact = Boolean(isSidebarRailCompact);
  // Expand is useless on forced-narrow viewports (stays compact anyway).
  const showExpandToggle = compact && !sidebarRailNarrowViewport && !isMobile;
  const showCollapseToggle = !compact && !isMobile;

  return (
    <div
      className={[
        'shell-header',
        className,
        compact ? 'is-rail-compact' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="shell-header__logo-row">
        {showExpandToggle ? (
          <Button
            iconKey="dock_to_right"
            iconSize={22}
            title="Expand menu"
            backgroundColor="transparent"
            borderColor="transparent"
            foregroundColor={theme.foregroundMuted}
            hoverBackgroundColor={theme.backgroundHover}
            hoverForegroundColor={theme.foreground}
            borderRadius="9px"
            height="40px"
            width="100%"
            padding="0"
            onClick={expandSidebarRail}
          />
        ) : null}

        {!compact ? (
          <>
            <LogoSmall homePath="/" />
            <div className="shell-header__logo-actions">
              {showCollapseToggle ? (
                <Button
                  iconKey="dock_to_left"
                  iconSize={20}
                  title="Collapse menu"
                  backgroundColor="transparent"
                  borderColor="transparent"
                  foregroundColor={theme.foregroundMuted}
                  hoverBackgroundColor={theme.backgroundHover}
                  hoverForegroundColor={theme.foreground}
                  borderRadius="9px"
                  height="32px"
                  width="32px"
                  padding="0"
                  onClick={collapseSidebarRail}
                />
              ) : null}
              {trailing}
            </div>
          </>
        ) : null}
      </div>

      {beforeSwitcher}

      {showBaseSwitcher ? (
        <div className="shell-header__switcher">
          <SidebarSwitcher />
        </div>
      ) : null}
      <ShellAddButton />
    </div>
  );
});

ShellHeader.displayName = 'ShellHeader';

export default ShellHeader;
